import React, { useEffect, useState } from 'react'
import ButtonStartIcon from '../../../../../button/ButtonStartIcon'
import Typography from '@mui/material/Typography'
import InputField from '../../../../../FormControl/input/InputField'
import Dialog from '../../../../../dialog/Dialog'
import useElements from '../../../../../../hooks/curriculum/useCurriculumElements'
import { Alert } from '@mui/material'
import InputColor from '../../../../../FormControl/input/InputColor'
import InputIcon from '../../../../../FormControl/input/InputIcon'
import { useNavigate, useParams } from 'react-router-dom'
import { validate } from '../../../../../../utils'
import isEmpty from 'lodash/isEmpty'

const ElementChildrenDialog = ({ 
  title, show, onClose,
  type, data = {},
  setChange,
  baseElement = {}
}) => {
  const navigate = useNavigate();
  const { curriculumId } = useParams();
  const types = ['create', 'edit', 'delete', 'add children', 'clone children', 'edit children', 'delete children']
  const { 
    errors: errorsData,
    createElementChildren,
    updateBasicInformationElementChildren,
    updateTypeElementsElementChildren,
    deleteElementChildren
  } = useElements()
  const [form, setInputForm] = useState({})
  const [errors, setInputErrors] = useState(errorsData)
  const [loading, toggleLoading] = useState(false)

  const onChange = (name, value) => {
    setInputForm({ ...form, [name]: value })
    setInputErrors({ ...errors, [name]: null })
  }

  const validateForm = (form) => {
    const fieldRules = {
      name: 'required',
      shortName: 'required|withoutSpaces'
    }

    const errors = validate(fieldRules, form)
    if (!isEmpty(errors)) {
      return { error: true, ...errors }
    }

    return { error: false }
  }

  const onSave = async (e) => {
    if (!types.includes(type))  {
      setInputErrors({ message: 'Invalid type' })
      return
    }
    e.preventDefault()
    toggleLoading(true)

    try {
      let result = { error: false } 
      if (type === 'create') {
        const res = validateForm(form)
        if (res.error) {
          setInputErrors(res)
          return
        }
        result = await createElementChildren(form)
      }
      if (type === 'edit') {
        result = await updateBasicInformationElementChildren(form._id, form)
      }
      if (type === 'delete') {
        result = await deleteElementChildren(form._id)
      }

      if (type === 'add children') {
        const res = validateForm(form)
        if (res.error) {
          setInputErrors(res)
          return
        }
        result = await updateTypeElementsElementChildren(baseElement._id, 'add', form.elementId, form.parent, form)
      }
      if (type === 'clone children') {
        result = await updateTypeElementsElementChildren(baseElement._id, 'clone', form.elementId, form.parent, form)
      }
      if (type === 'edit children') {
        const res = validateForm(form)
        if (res.error) {
          setInputErrors(res)
          return
        }
        result = await updateTypeElementsElementChildren(baseElement._id, 'edit', form._id, form.parent, form)
      }
      if (type === 'delete children') {
        result = await updateTypeElementsElementChildren(baseElement._id, 'delete', form._id, form.parent, form)
      }

      if (result.error) {
        setInputErrors(result)
      } else {
        onClose()
        if (type === 'delete') {
          navigate(`/dashboard/curriculum/${curriculumId}/elements`)
        }
        setChange((old) => !old)
      }
    } finally {
      toggleLoading(false)
    }
  }

  useEffect(() => {
    setInputErrors(errorsData)
  }, [errorsData])

  useEffect(() => {
    setInputForm(data || {})
  }, [data])

  const Actions = (
    <>
      <ButtonStartIcon
        onClick={onClose}
        color='primary'
        variant='outlined'
        disabled={loading}
        startIcon={["fal","times"]}
      >
        Cancel
      </ButtonStartIcon>
      { types.includes(type) &&
        <ButtonStartIcon
        onClick={onSave}
        color='primary'
        variant='contained'
        disabled={loading}
        loading={loading}
        startIcon={["fal","check"]}
      >
        { ['delete children', 'delete'].includes(type) ? 'Confirm' : 'Save'}
      </ButtonStartIcon>
      }
    </>
  );

  return (
    <Dialog
      id={`${type}-element`}
      show={show} 
      title={title ? title : `${type} children`}
      onClose={onClose}
      actions={Actions}
    >
      { ['delete children', 'delete'].includes(type)
        ? <Typography>Are you sure you want to delete the child element: <b>{form.name}</b>?</Typography>
        : ['clone children'].includes(type)
          ? <Typography>Are you sure you want to clone the child element: <b>{form.name}</b>?</Typography>
          : <div className='flex flex-col gap-4 py-4'>
            <div className='grid grid-cols-4 items-center gap-4'>
              <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
                Name
              </Typography>
              <div className='col-span-3'>
                { type === 'preview'
                  ? <Typography>
                      {form.name}
                    </Typography>
                  :  <InputField
                    id='name'
                    name='name'
                    value={form.name}
                    error={errors?.name}
                    helperText={errors?.name}
                    onChange={(e) => onChange("name", e.target.value)}
                    fullWidth
                    multiline={true}
                  />
                }
              </div>
              <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
                Short Name
              </Typography>
              <div className='col-span-3'>
                { type === 'preview'
                  ? <Typography>
                      {form.shortName}
                    </Typography>
                  :  <InputField
                    id='shortName'
                    name='shortName'
                    value={form.shortName}
                    error={errors?.shortName}
                    helperText={errors?.shortName}
                    onChange={(e) => onChange("shortName", e.target.value)}
                    fullWidth
                    multiline={true}
                  />
                }
              </div>
              <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
                Icon
              </Typography>
              <div className='col-span-3'>
                <InputIcon
                  id='icon'
                  value={form.icon}
                  error={errors?.icon}
                  disabled={type === 'preview'}
                  onChange={(value) => onChange("icon", value)}
                  fullWidth
                />
              </div>
              <Typography variant='inherit' className='font-bold pl-2' color='primary' align='right' component='p'>
                Color
              </Typography>
              <div>
                <InputColor
                  id='primaryColor'
                  name='primaryColor'
                  type="color"
                  value={form.primaryColor}
                  error={errors?.primaryColor}
                  disabled={type === 'preview'}
                  onChange={(value) => onChange("primaryColor", value)}
                  fullWidth
                />
              </div>
              {/* <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
                Secondary color
              </Typography>
              <div>
                <InputColor
                  id='secondaryColor'
                  name='secondaryColor'
                  type="color"
                  value={form.secondaryColor}
                  error={errors?.secondaryColor}
                  disabled={type === 'preview'}
                  onChange={(value) => onChange("secondaryColor", value)}
                  fullWidth
                />
              </div> */}
            </div>
          </div>
      }
      { errors && errors.message && 
        <Alert 
          severity="error"
          onClose={() => setInputErrors({ message: null })}
        >
          {errors.message}
        </Alert> 
      }
    </Dialog>
  )
}

export default ElementChildrenDialog